import { Injectable } from '@angular/core';
import {
  CreateOverrideSessionPayload,
  OverrideSessionActiveResponse,
  OverrideSessionDetailsResponse,
  OverrideSessionGetListResponse,
  OverrideSessionKeys,
  OverrideSessionListItemResponse,
  OverrideSessionMasterPaidCopyPayload,
  OverrideSessionMasterPaidDetailsResponse,
  OverrideSessionMasterPaidMissingProductsResponse,
  OverrideSessionResponse,
  OverrideSessionStateType,
  OverrideTemplateSessionTransactionsGetListResponse,
  UpdateOverrideSessionPayload,
} from '@ag-common-lib/public-api';
import { LoadOptions } from 'devextreme/common/data';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import { ReportingApiBlobResponse, ReportingApiService } from './reporting-api.service';

const OVERRIDE_REPORTING_GRID_PAGE_SIZE = 50;

@Injectable()
export class OverrideReportingSessionsService<
  TRow extends OverrideSessionListItemResponse = OverrideSessionListItemResponse,
> {
  private readonly basePath = '/api/override/session';
  private readonly gridDataSource = new DataSource({
    paginate: true,
    pageSize: OVERRIDE_REPORTING_GRID_PAGE_SIZE,
    store: new CustomStore({
      key: OverrideSessionKeys.id,
      load: async (loadOptions: LoadOptions): Promise<OverrideSessionGetListResponse> => {
        const response = await this.loadGridSessions(loadOptions);

        return {
          ...response,
          data: response.data.map(session => this.mapGridRow(session)) as TRow[],
        };
      },
    }),
  });
  private readonly activeGridDataSource = new DataSource({
    paginate: true,
    pageSize: OVERRIDE_REPORTING_GRID_PAGE_SIZE,
    store: new CustomStore({
      key: OverrideSessionKeys.id,
      load: async (loadOptions: LoadOptions): Promise<OverrideSessionGetListResponse> => {
        const response = await this.loadGridSessions(this.withActiveSessionsFilter(loadOptions));

        return {
          ...response,
          data: response.data.map(session => this.mapGridRow(session)) as TRow[],
        };
      },
    }),
  });

  constructor(private readonly reportingApiService: ReportingApiService) {}

  getDataSource(): DataSource<TRow, number> {
    return this.activeGridDataSource;
  }

  reloadDataSource(): Promise<void> {
    return this.activeGridDataSource.reload().then(() => undefined);
  }

  getList(loadOptions: LoadOptions | Record<string, unknown> = {}): Promise<OverrideSessionGetListResponse> {
    return this.loadGridSessions(loadOptions);
  }

  getTemplateTransactions(
    sessionId: number,
    templateId: number,
    loadOptions: LoadOptions | Record<string, unknown> = {},
  ): Promise<OverrideTemplateSessionTransactionsGetListResponse> {
    return this.reportingApiService.request<OverrideTemplateSessionTransactionsGetListResponse>(
      `${this.basePath}/${sessionId}/template/${templateId}/transactions/get-list`,
      {
        method: 'POST',
        body: this.normalizeTemplateTransactionsLoadOptions(loadOptions),
      },
    );
  }

  async getActiveSession(): Promise<OverrideSessionResponse | null> {
    const response = await this.reportingApiService.request<OverrideSessionActiveResponse>(`${this.basePath}/active`);
    return response.data ?? null;
  }

  async createSession(payload: CreateOverrideSessionPayload): Promise<OverrideSessionResponse> {
    const response = await this.reportingApiService.request<OverrideSessionDetailsResponse>(this.basePath, {
      method: 'POST',
      body: payload,
    });
    return response.data;
  }

  async updateSession(id: number, payload: UpdateOverrideSessionPayload): Promise<OverrideSessionResponse> {
    const response = await this.reportingApiService.request<OverrideSessionDetailsResponse>(`${this.basePath}/${id}`, {
      method: 'PUT',
      body: payload,
    });
    return response.data;
  }

  async finishSession(id: number): Promise<OverrideSessionResponse> {
    const response = await this.reportingApiService.request<OverrideSessionDetailsResponse>(
      `${this.basePath}/${id}/finish`,
      {
        method: 'POST',
      },
    );
    return response.data;
  }

  async copyMasterPaidRows(
    id: number,
    payload: OverrideSessionMasterPaidCopyPayload,
  ): Promise<OverrideSessionMasterPaidDetailsResponse['data']> {
    const response = await this.reportingApiService.request<OverrideSessionMasterPaidDetailsResponse>(
      `${this.basePath}/${id}/master-paid/copy`,
      {
        method: 'POST',
        body: payload,
      },
    );
    return response.data;
  }

  async importMasterPaidWorkbook(id: number, file: File): Promise<OverrideSessionMasterPaidDetailsResponse['data']> {
    const formData = new FormData();
    formData.append('file', file, file.name);

    const response = await this.reportingApiService.request<OverrideSessionMasterPaidDetailsResponse>(
      `${this.basePath}/${id}/master-paid/import`,
      {
        method: 'POST',
        body: formData,
      },
    );
    return response.data;
  }

  async getMasterPaidMissingProducts(id: number): Promise<OverrideSessionMasterPaidMissingProductsResponse['data']> {
    const response = await this.reportingApiService.request<OverrideSessionMasterPaidMissingProductsResponse>(
      `${this.basePath}/${id}/master-paid/missing-products`,
    );
    return response.data;
  }

  exportMasterPaidData(id: number): Promise<ReportingApiBlobResponse> {
    return this.reportingApiService.requestBlob(`${this.basePath}/${id}/master-paid/export`);
  }

  protected mapGridRow(session: OverrideSessionListItemResponse): TRow {
    return session as TRow;
  }

  private loadGridSessions(loadOptions: LoadOptions): Promise<OverrideSessionGetListResponse> {
    return this.reportingApiService.request<OverrideSessionGetListResponse>(`${this.basePath}/get-list`, {
      method: 'POST',
      body: this.normalizeLoadOptions(loadOptions),
    });
  }

  private withActiveSessionsFilter(loadOptions: LoadOptions): LoadOptions {
    const activeFilter = [OverrideSessionKeys.state, '<>', OverrideSessionStateType.draft];

    return {
      ...(loadOptions ?? {}),
      filter: loadOptions?.filter ? [loadOptions.filter, 'and', activeFilter] : activeFilter,
    };
  }

  private normalizeLoadOptions(
    loadOptions: LoadOptions | Record<string, unknown> | null | undefined,
  ): Record<string, unknown> {
    if (!loadOptions || typeof loadOptions !== 'object') {
      return {
        skip: 0,
        take: OVERRIDE_REPORTING_GRID_PAGE_SIZE,
        requireTotalCount: true,
        sort: [{ selector: OverrideSessionKeys.createdAt, desc: false }],
      };
    }

    const normalizedLoadOptions = Object.entries(loadOptions as Record<string, unknown>).reduce<
      Record<string, unknown>
    >((result, [key, value]) => {
      if (value === undefined) {
        return result;
      }

      result[key] = value;
      return result;
    }, {});

    normalizedLoadOptions.requireTotalCount = true;
    normalizedLoadOptions['skip'] ??= 0;
    normalizedLoadOptions['take'] ??= OVERRIDE_REPORTING_GRID_PAGE_SIZE;

    if (!normalizedLoadOptions['sort']) {
      normalizedLoadOptions['sort'] = [{ selector: OverrideSessionKeys.createdAt, desc: false }];
    }

    return normalizedLoadOptions;
  }

  private normalizeTemplateTransactionsLoadOptions(
    loadOptions: LoadOptions | Record<string, unknown> | null | undefined,
  ): Record<string, unknown> {
    if (!loadOptions || typeof loadOptions !== 'object') {
      return {
        skip: 0,
        take: OVERRIDE_REPORTING_GRID_PAGE_SIZE,
        requireTotalCount: true,
        sort: [{ selector: 'id', desc: false }],
      };
    }

    const normalizedLoadOptions = Object.entries(loadOptions as Record<string, unknown>).reduce<
      Record<string, unknown>
    >((result, [key, value]) => {
      if (value === undefined) {
        return result;
      }

      result[key] = value;
      return result;
    }, {});

    normalizedLoadOptions.requireTotalCount = true;
    normalizedLoadOptions['skip'] ??= 0;
    normalizedLoadOptions['take'] ??= OVERRIDE_REPORTING_GRID_PAGE_SIZE;

    if (!normalizedLoadOptions['sort']) {
      normalizedLoadOptions['sort'] = [{ selector: 'id', desc: false }];
    }

    return normalizedLoadOptions;
  }
}
